import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { categoryLabels, useCategories } from '../catalog/ItemForm';
import { formatPln } from '../catalog/money';
import './finance.css';

/** One row of "Urządzenia"; the purchase price and payback come only for a role that sees the catalog's purchase price. */
export type ItemEarning = { itemId: string; name: string; events: number; netGrosze: number; purchasePriceGrosze?: number | null; paybackBp?: number | null };
type SortKey = 'name' | 'events' | 'netGrosze' | 'purchasePriceGrosze' | 'paybackBp';

/** 10 000 bp → "100%", 1 250 → "12,5%" (tenths of a percent, halves up). */
export function paybackLabel(bp: number): string {
  // in whole tenths of a percent (integers only, halves up), so 12,45% and 12,55% round alike
  const sign = bp < 0 ? '−' : '';
  const tenths = Math.floor((Math.abs(bp) + 5) / 10);
  const rest = tenths % 10;
  return `${sign}${Math.floor(tenths / 10)}${rest === 0 ? '' : `,${rest}`}%`;
}

/** Sorted by `key` (names in Polish order), empty values always last. */
export function sortEarnings(rows: readonly ItemEarning[], key: SortKey, desc: boolean): ItemEarning[] {
  return [...rows].sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    const c = typeof x === 'string' ? x.localeCompare(y as string, 'pl') : (x as number) - (y as number);
    return desc ? -c : c;
  });
}

const COLUMNS: Array<{ key: SortKey; label: string; num: boolean; purchase?: boolean }> = [
  { key: 'name', label: 'Urządzenie', num: false },
  { key: 'events', label: 'Wydarzenia', num: true },
  { key: 'netGrosze', label: 'Zarobiło netto', num: true },
  { key: 'purchasePriceGrosze', label: 'Cena zakupu', num: true, purchase: true },
  { key: 'paybackBp', label: 'Zwrot %', num: true, purchase: true },
];

/**
 * "Finanse → Więcej → Urządzenia" (F42, spec E3 §6.8): per catalog item in the chosen months, how many events it played,
 * what it earned (accepted quotes, after discounts) and — for whoever sees the purchase price — how much of it came back.
 */
export function ItemsPage() {
  const { me } = useMe();
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01`);
  const [to, setTo] = useState(`${year}-12`);
  const [categoryId, setCategoryId] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'netGrosze', desc: true });
  const categories = useCategories(me ? can(me, 'catalog.categories', 'view') : false);
  const valid = /^\d{4}-\d{2}$/.test(from) && /^\d{4}-\d{2}$/.test(to) && from <= to;
  const params = new URLSearchParams({ from, to, ...(categoryId ? { categoryId } : {}) });
  const report = useQuery({ queryKey: ['finance', 'items', from, to, categoryId], enabled: valid, queryFn: () => api<{ items: ItemEarning[] }>(`/api/finance/items?${params.toString()}`) });
  if (!me) return null;
  const rows = report.data?.items ?? [];
  const withPurchase = rows.some((r) => 'purchasePriceGrosze' in r);
  const columns = COLUMNS.filter((c) => !c.purchase || withPurchase);
  const sorted = sortEarnings(rows, sort.key, sort.desc);
  const pick = (key: SortKey) => setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key !== 'name' }));
  return (
    <section className="page wide" aria-label="Urządzenia" data-feature-id="finance.items">
      <p className="lede">Ile razy sprzęt zagrał i ile zarobił (kosztorysy: zaakceptowana wersja, a bez niej najnowsza; po rabatach){withPurchase ? ' — i jaka część ceny zakupu już wróciła' : ''}.</p>
      <div className="fin-add" role="group" aria-label="Okres i kategoria">
        <label className="field">
          <span>Od miesiąca</span>
          <input type="month" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>Do miesiąca</span>
          <input type="month" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {categories.data && (
          <label className="field">
            <span>Kategoria</span>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Wszystkie</option>
              {categoryLabels(categories.data.categories).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {!valid && <p className="fin-warn">„Od” nie może być później niż „Do”.</p>}
      {report.error && (
        <p className="alert" role="alert">
          {report.error instanceof ApiError ? report.error.message : 'Nie udało się wczytać zestawienia'}
        </p>
      )}
      {report.data && rows.length === 0 && <p className="muted">W tym okresie żaden sprzęt nie był w kosztorysie.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table" aria-label="Rentowność sprzętu">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={c.num ? 'num' : undefined} aria-sort={sort.key === c.key ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
                    <button className="sort-head" type="button" onClick={() => pick(c.key)}>
                      {c.label}
                      {sort.key === c.key ? (sort.desc ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.itemId}>
                  <td>{r.name}</td>
                  <td className="num">{r.events}</td>
                  <td className="num nowrap">{formatPln(r.netGrosze)}</td>
                  {withPurchase && (
                    <>
                      <td className="num nowrap">{r.purchasePriceGrosze == null ? '—' : formatPln(r.purchasePriceGrosze)}</td>
                      <td className="num nowrap">{r.paybackBp == null ? '—' : paybackLabel(r.paybackBp)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
